import React from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { StateSubscriptionProvider } from "@/contexts/StateSubscriptionContext";
import { AdminAuthProvider } from "@/contexts/AdminAuthContext";
import { SupportModeProvider } from "@/contexts/SupportModeContext";
import { AppLayout } from "@/components/AppLayout";
import { AdminGuard } from "@/components/admin/AdminGuard";
import { AdminShell } from "@/components/admin/AdminShell";
import { useDeepLinks } from "@/hooks/useDeepLinks";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import AcceptInvite from "./pages/AcceptInvite";
import CompleteProfile from "./pages/CompleteProfile";
import Dashboard from "./pages/Dashboard";
import Jobs from "./pages/Jobs";
import CreateJob from "./pages/CreateJob";
import JobDetail from "./pages/JobDetail";
import JobWorkflow from "./pages/JobWorkflow";
import Profile from "./pages/Profile";
import VerifyEmail from "./pages/VerifyEmail";
import NotFound from "./pages/NotFound";
// Notifications page removed - functionality covered by Permit Ops Center queue
// ComplianceDashboard removed from nav - compliance checks moved to permit detail
// Jurisdictions page removed - lookup integrated into permit detail
// Inspections page deprecated - functionality moved to Jobs page
// Community page removed - community data is embedded via ContextualTips
// Reports page merged into Ops Center - route redirects to /permits
import EvidenceVault from "./pages/EvidenceVault";
import PermitOpsCenter from "./pages/PermitOpsCenter";
import Wizard from "./pages/Wizard";
import QuickCreate from "./pages/QuickCreate";
// Multi-unit development project pages
import Projects from "./pages/Projects";
import ProjectDetail from "./pages/ProjectDetail";
// Admin pages
import AdminOverview from "./pages/admin/AdminOverview";
import AdminOrgs from "./pages/admin/AdminOrgs";
import AdminOrgDetail from "./pages/admin/AdminOrgDetail";
import AdminRuns from "./pages/admin/AdminRuns";
import AdminModeration from "./pages/admin/AdminModeration";
import AdminAuditLog from "./pages/admin/AdminAuditLog";
import FormAnalyzer from "./pages/admin/FormAnalyzer";

const queryClient = new QueryClient();

// Component to handle deep links - must be inside BrowserRouter
function DeepLinkHandler({ children }: { children: React.ReactNode }) {
  useDeepLinks();
  return <>{children}</>;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider>
      <AuthProvider>
        <AdminAuthProvider>
          <SupportModeProvider>
            <StateSubscriptionProvider>
              <TooltipProvider>
                <Toaster />
                <Sonner />
                <BrowserRouter>
                  <DeepLinkHandler>
                    <Routes>
                      <Route path="/" element={<Index />} />
                      <Route path="/auth" element={<Auth />} />
                      <Route path="/accept-invite" element={<AcceptInvite />} />
                      <Route path="/complete-profile" element={<CompleteProfile />} />
                      <Route path="/email-verify" element={<VerifyEmail />} />
                      <Route path="/dashboard" element={<AppLayout><Dashboard /></AppLayout>} />
                      {/* Permit Ops Center - main dashboard (TurboTax "Ops Center") */}
                      <Route path="/permits" element={<AppLayout><PermitOpsCenter /></AppLayout>} />
                      {/* Compliance - removed from nav, redirect to Ops Center (compliance checks in permit detail) */}
                      <Route path="/compliance" element={<Navigate to="/permits" replace />} />
                      {/* Jurisdictions page removed - lookup integrated into ComplianceDashboard */}
                      {/* Community page removed - data embedded via ContextualTips */}
                      {/* Inspections page removed - redirect to Jobs */}
                      <Route path="/inspections" element={<Navigate to="/jobs" replace />} />
                      {/* Reports merged into Ops Center - redirect */}
                      <Route path="/reports" element={<Navigate to="/permits" replace />} />
                      <Route path="/documents" element={<AppLayout><EvidenceVault /></AppLayout>} />
                      {/* Job routes - Wizard-First approach */}
                      <Route path="/jobs" element={<AppLayout><Jobs /></AppLayout>} />
                      <Route path="/jobs/new" element={<AppLayout><CreateJob /></AppLayout>} />
                      {/* Quick Create - Document-First Verification flow */}
                      <Route path="/quick-create" element={<QuickCreate />} />
                      {/* Primary route: Wizard is the default when clicking a job */}
                      <Route path="/jobs/:id" element={<JobWorkflow />} />
                      {/* Secondary route: Details page for reference/read-only view */}
                      <Route path="/jobs/:id/details" element={<AppLayout><JobDetail /></AppLayout>} />
                      {/* Backwards compat: redirect old /workflow URLs to new primary route */}
                      <Route path="/jobs/:id/workflow" element={<Navigate to="../" replace />} />
                      {/* Development Project routes (multi-unit) */}
                      <Route path="/projects" element={<AppLayout><Projects /></AppLayout>} />
                      {/* Project creation consolidated into unified wizard */}
                      <Route path="/projects/new" element={<Navigate to="/wizard" replace />} />
                      <Route path="/projects/:id" element={<AppLayout><ProjectDetail /></AppLayout>} />
                      {/* Wizard route - TurboTax-style guided permit intake */}
                      <Route path="/wizard" element={<Wizard />} />
                      {/* System routes */}
                      {/* Notifications page removed - redirect to My Work */}
                      <Route path="/notifications" element={<Navigate to="/permits" replace />} />
                      <Route path="/profile" element={<AppLayout><Profile /></AppLayout>} />
                                            {/* Admin routes - hidden from non-admins (shows 404) */}
                      <Route path="/admin" element={<AdminGuard><AdminShell /></AdminGuard>}>
                        <Route index element={<AdminOverview />} />
                        <Route path="orgs" element={<AdminOrgs />} />
                        <Route path="orgs/:orgId" element={<AdminOrgDetail />} />
                        <Route path="runs" element={<AdminRuns />} />
                        <Route path="moderation" element={<AdminModeration />} />
                        <Route path="audit" element={<AdminGuard requiredRole="superadmin"><AdminAuditLog /></AdminGuard>} />
                        <Route path="form-analyzer" element={<FormAnalyzer />} />
                        <Route path="form-analyzer/:schemaId" element={<FormAnalyzer />} />
                        <Route path="form-analyzer/:schemaId/edit" element={<FormAnalyzer />} />
                      </Route>
                      {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </DeepLinkHandler>
                </BrowserRouter>
              </TooltipProvider>
            </StateSubscriptionProvider>
          </SupportModeProvider>
        </AdminAuthProvider>
      </AuthProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
